export type CellRange = {
  startRow: number;
  startColumn: number;
  endRow: number;
  endColumn: number;
};

export type CellStyle = {
  fontFamily?: "Arial" | "Georgia" | "monospace";
  fontSize?: number;
  textColor?: string;
  fill?: string;
  border?: string;
  align?: "left" | "center" | "right";
  bold?: boolean;
  italic?: boolean;
};

export type SavedTableView = {
  filterColumn: number | null;
  filterValue: string;
  groupColumn: number | null;
};

export type TableMeta = Record<string, unknown>;
const color = /^#[\da-f]{6}$/i;
const cellKey = (row: number, column: number) => `${row}:${column}`;

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function normalizeCellRange(
  range: CellRange,
  rows: string[][],
): CellRange {
  const lastRow = Math.max(0, rows.length - 1),
    lastColumn = Math.max(0, (rows[0]?.length ?? 1) - 1),
    clamp = (value: number, max: number) =>
      Math.max(0, Math.min(max, Math.floor(Number(value) || 0)));
  const aRow = clamp(range.startRow, lastRow),
    bRow = clamp(range.endRow, lastRow),
    aColumn = clamp(range.startColumn, lastColumn),
    bColumn = clamp(range.endColumn, lastColumn);
  return {
    startRow: Math.min(aRow, bRow),
    endRow: Math.max(aRow, bRow),
    startColumn: Math.min(aColumn, bColumn),
    endColumn: Math.max(aColumn, bColumn),
  };
}

export function rangeCellCount(range: CellRange) {
  return (
    (range.endRow - range.startRow + 1) *
    (range.endColumn - range.startColumn + 1)
  );
}

function checkedCellStyle(value: unknown): CellStyle {
  const style = object(value),
    result: CellStyle = {};
  if (style.fontFamily !== undefined) {
    if (!["Arial", "Georgia", "monospace"].includes(String(style.fontFamily)))
      throw new Error("Invalid table cell font.");
    result.fontFamily = style.fontFamily as CellStyle["fontFamily"];
  }
  if (style.fontSize !== undefined) {
    const size = Number(style.fontSize);
    if (!Number.isFinite(size) || size < 10 || size > 32)
      throw new Error("Invalid table cell font size.");
    result.fontSize = size;
  }
  for (const key of ["textColor", "fill", "border"] as const) {
    if (style[key] !== undefined) {
      if (!color.test(String(style[key])))
        throw new Error("Invalid table cell color.");
      result[key] = String(style[key]);
    }
  }
  if (style.align !== undefined) {
    if (!["left", "center", "right"].includes(String(style.align)))
      throw new Error("Invalid table cell alignment.");
    result.align = style.align as CellStyle["align"];
  }
  if (style.bold !== undefined) result.bold = !!style.bold;
  if (style.italic !== undefined) result.italic = !!style.italic;
  return result;
}

export function tableCellStyles(meta: TableMeta) {
  const source = object(meta.cellStyles),
    styles: Record<string, CellStyle> = {};
  for (const [key, value] of Object.entries(source))
    styles[key] = checkedCellStyle(value);
  return styles;
}

export function cellStyleAt(meta: TableMeta, row: number, column: number) {
  return tableCellStyles(meta)[cellKey(row, column)] ?? {};
}

export function applyCellStyle(
  meta: TableMeta,
  rows: string[][],
  requested: CellRange,
  patch: Partial<CellStyle>,
) {
  const range = normalizeCellRange(requested, rows);
  if (rangeCellCount(range) > 500)
    throw new Error(
      "Style up to 500 cells at once, or use smaller ranges for this board object.",
    );
  const styles = tableCellStyles(meta);
  for (let row = range.startRow; row <= range.endRow; row++)
    for (let column = range.startColumn; column <= range.endColumn; column++) {
      const key = cellKey(row, column),
        next = checkedCellStyle({ ...styles[key], ...patch });
      if (Object.keys(next).length) styles[key] = next;
      else delete styles[key];
    }
  return { ...meta, cellStyles: styles };
}

export function remapCellStyles(
  meta: TableMeta,
  map: (row: number, column: number) => [number, number] | null,
) {
  const styles: Record<string, CellStyle> = {};
  for (const [key, style] of Object.entries(tableCellStyles(meta))) {
    const match = /^(\d+):(\d+)$/.exec(key);
    if (!match) continue;
    const next = map(Number(match[1]), Number(match[2]));
    if (next) styles[cellKey(next[0], next[1])] = style;
  }
  return { ...meta, cellStyles: styles };
}

export function savedTableView(
  meta: TableMeta,
  rows: string[][],
): SavedTableView {
  const source = object(meta.tableView),
    columns = rows[0]?.length ?? 0,
    column = (value: unknown) => {
      const number = Number(value);
      return Number.isInteger(number) && number >= 0 && number < columns
        ? number
        : null;
    };
  return {
    filterColumn: column(source.filterColumn),
    filterValue:
      typeof source.filterValue === "string"
        ? source.filterValue.slice(0, 120)
        : "",
    groupColumn: column(source.groupColumn),
  };
}

export type DisplayTableRow =
  | { kind: "group"; label: string }
  | {
      kind: "row";
      sourceIndex: number;
      row: string[];
      searchMatch: boolean;
    };

export function displayTableRows(
  rows: string[][],
  headers: boolean,
  meta: TableMeta,
  search = "",
): DisplayTableRow[] {
  const view = savedTableView(meta, rows),
    needle = view.filterValue.trim().toLocaleLowerCase(),
    local = search.trim().toLocaleLowerCase(),
    start = headers ? 1 : 0;
  let entries = rows.slice(start).map((row, offset) => ({
    row,
    sourceIndex: start + offset,
  }));
  if (needle && view.filterColumn !== null)
    entries = entries.filter(({ row }) =>
      (row[view.filterColumn!] || "").toLocaleLowerCase().includes(needle),
    );
  if (view.groupColumn !== null)
    entries = entries
      .map((entry, order) => ({ ...entry, order }))
      .sort((a, b) => {
        const grouped = new Intl.Collator(undefined, {
          numeric: true,
          sensitivity: "base",
        }).compare(
          a.row[view.groupColumn!] || "",
          b.row[view.groupColumn!] || "",
        );
        return grouped || a.order - b.order;
      });
  const result: DisplayTableRow[] = [];
  let group: string | undefined;
  for (const entry of entries) {
    if (view.groupColumn !== null) {
      const next = entry.row[view.groupColumn] || "Blank";
      if (next !== group) {
        group = next;
        result.push({ kind: "group", label: next });
      }
    }
    result.push({
      kind: "row",
      sourceIndex: entry.sourceIndex,
      row: entry.row,
      searchMatch:
        !local ||
        entry.row.some((cell) => cell.toLocaleLowerCase().includes(local)),
    });
  }
  return result;
}

function sizes(meta: TableMeta, key: "columnWidths" | "rowHeights") {
  const value = meta[key];
  return Array.isArray(value)
    ? value.map((item) => Number(item)).filter(Number.isFinite)
    : [];
}

export function tableColumnWidths(meta: TableMeta, count: number) {
  const value = sizes(meta, "columnWidths");
  return Array.from({ length: count }, (_, index) =>
    Math.max(60, Math.min(2000, value[index] || 160)),
  );
}

export function tableRowHeights(
  meta: TableMeta,
  count: number | string[][],
  grid: boolean,
) {
  const value = sizes(meta, "rowHeights"),
    fallback = grid ? 72 : 38;
  const rows = Array.isArray(count) ? count : undefined;
  const widths = rows ? tableColumnWidths(meta, rows[0]?.length ?? 0) : [];
  return Array.from({ length: rows?.length ?? Number(count) }, (_, index) => {
    let height = Math.max(34, Math.min(10000, value[index] || fallback));
    if (rows && meta.autoFitRows === true)
      for (const [column, text] of rows[index].entries()) {
        const style = cellStyleAt(meta, index, column),
          font = style.fontSize ?? (Number(meta.fontSize) || 14);
        const chars = Math.max(
          1,
          Math.floor((widths[column] - 20) / (font * 1.1)),
        );
        const lines = text
          .split("\n")
          .reduce(
            (n, line) => n + Math.max(1, Math.ceil(line.length / chars)),
            0,
          );
        const needed = Math.ceil(lines * font * 1.5 + 22);
        if (needed > 10000)
          throw new Error(
            "Table text needs a taller row; widen the column or split the text",
          );
        height = Math.max(height, needed);
      }
    return height;
  });
}

export function tableObjectHeight(
  rows: string[][],
  meta: TableMeta,
  grid: boolean,
) {
  const gaps = grid ? 6 + 5 * (rows.length + 1) : 0,
    groups = displayTableRows(rows, false, meta).filter(
      (entry) => entry.kind === "group",
    ).length;
  return (
    44 +
    tableRowHeights(meta, rows, grid).reduce((a, b) => a + b, 0) +
    gaps +
    groups * 28
  );
}

export function validateTableMetadata(rows: string[][], meta: TableMeta) {
  const columns = rows[0]?.length ?? 0,
    styles = tableCellStyles(meta);
  if (Object.keys(styles).length > 500)
    throw new Error("This table has too many individually styled cells.");
  for (const key of Object.keys(styles)) {
    const match = /^(\d+):(\d+)$/.exec(key);
    if (
      !match ||
      Number(match[1]) >= rows.length ||
      Number(match[2]) >= columns
    )
      throw new Error("Invalid table cell style position.");
  }
  const widths = sizes(meta, "columnWidths"),
    heights = sizes(meta, "rowHeights");
  if (
    widths.length > columns ||
    widths.some((value) => value < 60 || value > 2000) ||
    heights.length > rows.length ||
    heights.some((value) => value < 34 || value > 10000)
  )
    throw new Error("Invalid table row or column size.");
  const view = object(meta.tableView);
  if (
    (view.filterValue !== undefined &&
      (typeof view.filterValue !== "string" ||
        view.filterValue.length > 120)) ||
    [view.filterColumn, view.groupColumn].some(
      (value) =>
        value !== undefined &&
        value !== null &&
        (!Number.isInteger(value) ||
          Number(value) < 0 ||
          Number(value) >= columns),
    )
  )
    throw new Error("Invalid saved table view.");
}

export type TableModel = { rows: string[][]; meta: TableMeta };

function replaceSizes(
  meta: TableMeta,
  key: "columnWidths" | "rowHeights",
  value: number[],
) {
  return { ...meta, [key]: value };
}

function remapViewColumns(
  meta: TableMeta,
  map: (column: number) => number | null,
) {
  const view = object(meta.tableView),
    next = (value: unknown) =>
      Number.isInteger(value) && Number(value) >= 0 ? map(Number(value)) : null;
  return {
    ...meta,
    tableView: {
      filterValue:
        typeof view.filterValue === "string"
          ? view.filterValue.slice(0, 120)
          : "",
      filterColumn: next(view.filterColumn),
      groupColumn: next(view.groupColumn),
    },
  };
}

export function insertTableRow(
  rows: string[][],
  meta: TableMeta,
  index: number,
): TableModel {
  const at = Math.max(0, Math.min(rows.length, index)),
    nextRows = rows.map((row) => [...row]);
  nextRows.splice(at, 0, Array(rows[0].length).fill(""));
  let next: TableMeta = remapCellStyles(meta, (row, column) => [
    row >= at ? row + 1 : row,
    column,
  ]);
  const heights = tableRowHeights(meta, rows.length, false);
  heights.splice(at, 0, 38);
  next = replaceSizes(next, "rowHeights", heights);
  return { rows: nextRows, meta: next };
}

export function deleteTableRow(
  rows: string[][],
  meta: TableMeta,
  index: number,
): TableModel {
  if (rows.length <= 1) return { rows, meta };
  const at = Math.max(0, Math.min(rows.length - 1, index)),
    nextRows = rows.filter((_, row) => row !== at);
  let next: TableMeta = remapCellStyles(meta, (row, column) =>
    row === at ? null : [row > at ? row - 1 : row, column],
  );
  const heights = tableRowHeights(meta, rows.length, false);
  heights.splice(at, 1);
  next = replaceSizes(next, "rowHeights", heights);
  return { rows: nextRows, meta: next };
}

export function moveTableRow(
  rows: string[][],
  meta: TableMeta,
  from: number,
  to: number,
): TableModel {
  if (from === to || !rows[from] || !rows[to]) return { rows, meta };
  const nextRows = rows.map((row) => [...row]);
  [nextRows[from], nextRows[to]] = [nextRows[to], nextRows[from]];
  let next: TableMeta = remapCellStyles(meta, (row, column) => [
    row === from ? to : row === to ? from : row,
    column,
  ]);
  const heights = tableRowHeights(meta, rows.length, false);
  [heights[from], heights[to]] = [heights[to], heights[from]];
  next = replaceSizes(next, "rowHeights", heights);
  return { rows: nextRows, meta: next };
}

export function insertTableColumn(
  rows: string[][],
  meta: TableMeta,
  index: number,
): TableModel {
  const at = Math.max(0, Math.min(rows[0].length, index)),
    nextRows = rows.map((row) => {
      const next = [...row];
      next.splice(at, 0, "");
      return next;
    });
  let next: TableMeta = remapCellStyles(meta, (row, column) => [
    row,
    column >= at ? column + 1 : column,
  ]);
  const widths = tableColumnWidths(meta, rows[0].length);
  widths.splice(at, 0, 160);
  next = replaceSizes(next, "columnWidths", widths);
  next = remapViewColumns(next, (column) =>
    column >= at ? column + 1 : column,
  );
  return { rows: nextRows, meta: next };
}

export function deleteTableColumn(
  rows: string[][],
  meta: TableMeta,
  index: number,
): TableModel {
  if (rows[0].length <= 1) return { rows, meta };
  const at = Math.max(0, Math.min(rows[0].length - 1, index)),
    nextRows = rows.map((row) => row.filter((_, column) => column !== at));
  let next: TableMeta = remapCellStyles(meta, (row, column) =>
    column === at ? null : [row, column > at ? column - 1 : column],
  );
  const widths = tableColumnWidths(meta, rows[0].length);
  widths.splice(at, 1);
  next = replaceSizes(next, "columnWidths", widths);
  next = remapViewColumns(next, (column) =>
    column === at ? null : column > at ? column - 1 : column,
  );
  return { rows: nextRows, meta: next };
}

export function moveTableColumn(
  rows: string[][],
  meta: TableMeta,
  from: number,
  to: number,
): TableModel {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= rows[0].length ||
    to >= rows[0].length
  )
    return { rows, meta };
  const nextRows = rows.map((row) => {
    const next = [...row];
    [next[from], next[to]] = [next[to], next[from]];
    return next;
  });
  let next: TableMeta = remapCellStyles(meta, (row, column) => [
    row,
    column === from ? to : column === to ? from : column,
  ]);
  const widths = tableColumnWidths(meta, rows[0].length);
  [widths[from], widths[to]] = [widths[to], widths[from]];
  next = replaceSizes(next, "columnWidths", widths);
  next = remapViewColumns(next, (column) =>
    column === from ? to : column === to ? from : column,
  );
  return { rows: nextRows, meta: next };
}

export function sortTableModel(
  rows: string[][],
  meta: TableMeta,
  column: number,
  ascending: boolean,
  headers: boolean,
): TableModel {
  const start = headers ? 1 : 0,
    compare = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    }),
    ordered = rows
      .map((row, index) => ({ row: [...row], index }))
      .slice(start)
      .sort((a, b) => {
        const result = compare.compare(
          a.row[column] || "",
          b.row[column] || "",
        );
        return result ? result * (ascending ? 1 : -1) : a.index - b.index;
      }),
    entries = [
      ...(headers ? [{ row: [...rows[0]], index: 0 }] : []),
      ...ordered,
    ],
    destination = new Map(entries.map((entry, index) => [entry.index, index]));
  let next: TableMeta = remapCellStyles(meta, (row, currentColumn) => [
    destination.get(row) ?? row,
    currentColumn,
  ]);
  const heights = tableRowHeights(meta, rows.length, false),
    sortedHeights = entries.map((entry) => heights[entry.index]);
  next = replaceSizes(next, "rowHeights", sortedHeights);
  return { rows: entries.map((entry) => entry.row), meta: next };
}

/** Row replacement keeps presentation by index, or an explicit new-to-old row map. */
export function replaceTableRows(
  oldRows: string[][],
  rows: string[][],
  meta: TableMeta,
  rowMap?: (number | null)[],
): TableMeta {
  if (
    rowMap &&
    (rowMap.length !== rows.length ||
      rowMap.some((n) => n !== null && n >= oldRows.length) ||
      new Set(rowMap.filter((n) => n !== null)).size !==
        rowMap.filter((n) => n !== null).length)
  )
    throw new Error("Invalid row identity map");
  const map = rowMap ?? rows.map((_, n) => (n < oldRows.length ? n : null));
  const next = remapCellStyles(meta, (r, c) => {
    const n = map.indexOf(r);
    return n < 0 || c >= rows[0].length ? null : [n, c];
  });
  const widths = tableColumnWidths(meta, rows[0].length),
    heights = tableRowHeights(meta, oldRows.length, false);
  return {
    ...next,
    columnWidths: widths,
    rowHeights: map.map((n) => (n === null ? 38 : heights[n])),
    tableView: savedTableView(meta, rows),
  };
}
